/*
 * Profile → "Ticket Credit" (account-level; the Apex postponement retention
 * credit). Pure presentational — Profile owns the read (useTicketCredits) and
 * passes the view in:
 *   null (flag off / 404 / any failed read)  → NOTHING
 *   available:false                          → one quiet "couldn't load" line
 *   available:true, no credits               → NOTHING
 *   otherwise one card per credit, in API order (pre-sorted server-side).
 * Active cards are full (amount, code + Copy, event line, use-by, how-to-use);
 * used / expired / withdrawn cards collapse to one line (amount + status
 * badge) and NEVER show the code: a code that can't be used is not printed.
 * Status is always spelled out in text, never colour alone. Amount and code
 * are pinned dir="ltr" so they read correctly inside the RTL (Arabic) layout.
 */
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import { formatMinorAmount } from '../data/legacyHistory'
import { formatUseBy, type TicketCredit, type TicketCreditsView } from '../data/ticketCredits'

const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em', fontWeight: 400,
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15,
}
const codeChipStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, letterSpacing: '.04em',
  background: 'var(--bg-frame)', border: '1px solid var(--hairline-14)', borderRadius: 8,
  padding: '4px 9px', color: 'var(--text)', overflowWrap: 'anywhere', userSelect: 'all',
}
/** canon cmp.button secondary: surface background + border (matches the
 *  app's existing secondary buttons — softbtn ink, hairline-14 border). The
 *  browser's focus ring is left intact for visible keyboard focus. */
const secondaryButtonStyle: CSSProperties = {
  border: '1px solid var(--hairline-14)', background: 'var(--surface)', color: 'var(--text-softbtn)',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  minHeight: 40, borderRadius: 10, padding: '0 14px', flex: 'none',
}
const srOnly: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}
const lineStyle: CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }

const badgeStyle = (status: TicketCredit['status']): CSSProperties => ({
  display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 9px',
  ...(status === 'active'
    ? { background: 'var(--tint-teal)', color: 'var(--accent-bright)', border: '1px solid var(--accent)' }
    : status === 'redeemed'
      ? { background: 'var(--hairline-08)', color: 'var(--text-secondary)', border: '1px solid var(--hairline-14)' }
      : { background: 'transparent', color: 'var(--text-dim)', border: '1px dashed var(--hairline-14)' }),
})

function statusText(c: TicketCredit, t: TranslateFn): string {
  if (c.status === 'active') return t('ticketCredit.status.active')
  if (c.status === 'expired') return t('ticketCredit.status.expired')
  if (c.status === 'revoked') return t('ticketCredit.status.revoked')
  return c.redeemed_order_id
    ? t('ticketCredit.status.redeemedOnOrder', { order: c.redeemed_order_id })
    : t('ticketCredit.status.redeemed')
}

function StatusBadge({ credit, t }: { credit: TicketCredit; t: TranslateFn }) {
  return (
    <span data-testid="account-ticket-credit-status" data-status={credit.status} style={badgeStyle(credit.status)}>
      {statusText(credit, t)}
    </span>
  )
}

function CopyButton({ code, codeRef, t }: { code: string; codeRef: RefObject<HTMLElement | null>; t: TranslateFn }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const selectCode = () => {
    // Fallback: select the code text so the user can copy it by hand.
    try {
      const el = codeRef.current
      const sel = typeof window !== 'undefined' ? window.getSelection() : null
      if (!el || !sel) return
      const range = document.createRange()
      range.selectNodeContents(el)
      sel.removeAllRanges()
      sel.addRange(range)
    } catch {
      /* selection unavailable — nothing more to do */
    }
  }

  const onClick = () => {
    const showCopied = () => {
      setCopied(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    }
    let pending: Promise<void> | undefined
    try {
      // Called synchronously inside the click so the user activation holds.
      pending = navigator.clipboard?.writeText(code)
    } catch {
      pending = undefined
    }
    if (!pending) { selectCode(); return }
    pending.then(showCopied, selectCode)
  }

  return (
    <>
      <button
        type="button"
        data-testid="account-ticket-credit-copy"
        aria-label={copied ? t('ticketCredit.copied') : t('ticketCredit.copyLabel', { code })}
        onClick={onClick}
        style={secondaryButtonStyle}
      >
        {copied ? t('ticketCredit.copied') : t('ticketCredit.copy')}
      </button>
      <span role="status" style={srOnly}>{copied ? t('ticketCredit.copied') : ''}</span>
    </>
  )
}

function CreditCard({ credit }: { credit: TicketCredit }) {
  const { t, lang } = useLocale()
  const codeRef = useRef<HTMLElement | null>(null)
  const amount = formatMinorAmount(credit.amount_minor, credit.currency_iso, lang)

  if (credit.status !== 'active' || !credit.code) {
    return (
      <div
        data-testid="account-ticket-credit-card"
        data-status={credit.status}
        style={{ ...cardStyle, padding: '10px 15px', marginBottom: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}
      >
        <span dir="ltr" data-testid="account-ticket-credit-amount" style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-muted)' }}>{amount}</span>
        <StatusBadge credit={credit} t={t} />
      </div>
    )
  }

  const brands = credit.brand_names.join(t('ticketCredit.brandsOr'))
  return (
    <div data-testid="account-ticket-credit-card" data-status={credit.status} style={{ ...cardStyle, padding: 15, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <span dir="ltr" data-testid="account-ticket-credit-amount" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{amount}</span>
        <StatusBadge credit={credit} t={t} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <code ref={codeRef} dir="ltr" data-testid="account-ticket-credit-code" style={codeChipStyle}>{credit.code}</code>
        <CopyButton code={credit.code} codeRef={codeRef} t={t} />
      </div>
      {credit.for_event_name && (
        <div data-testid="account-ticket-credit-event" style={{ ...lineStyle, marginTop: 12, color: 'var(--text)' }}>
          {t('ticketCredit.forEvent', { event: credit.for_event_name })}
        </div>
      )}
      <div data-testid="account-ticket-credit-useby" style={{ ...lineStyle, marginTop: credit.for_event_name ? 2 : 12 }}>
        {t('ticketCredit.useBy', { date: formatUseBy(credit.expires_at, lang) })}
      </div>
      {/* FLAGGED COPY — pending Duncan's review (how-to-use wording). Display
          copy only: no leftover-amount logic lives in this app. */}
      <div data-testid="account-ticket-credit-howto" style={{ fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.5, marginTop: 10 }}>
        {brands ? t('ticketCredit.howTo', { brands }) : t('ticketCredit.howToNoBrands')}
      </div>
    </div>
  )
}

export function TicketCreditsSection({ view }: { view: TicketCreditsView | null }) {
  const { t } = useLocale()
  if (!view) return null
  if (view.available && view.credits.length === 0) return null
  return (
    <section id="profile.ticketCredit" data-testid="account-ticket-credit-section" aria-labelledby="account-ticket-credit-heading" style={{ marginBottom: 22 }}>
      <h2 id="account-ticket-credit-heading" style={headingStyle}>{t('ticketCredit.heading')}</h2>
      {!view.available ? (
        <div data-testid="account-ticket-credit-unavailable" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px' }}>
          {t('ticketCredit.unavailable')}
        </div>
      ) : (
        view.credits.map((c, i) => <CreditCard key={`${c.code ?? c.status}-${i}`} credit={c} />)
      )}
    </section>
  )
}
